import { z } from 'zod';
import { BuyEventType, BuyEventTypePayloadMap, BuyEventSchemaMap } from './events/Buy';
import { ExampleEventType, ExampleEventTypePayloadMap, ExampleEventSchemaMap } from './events/ExampleEvents';
import { FindMyOrderEventSchemaMap, FindMyOrderEventType, FindMyOrderEventTypePayloadMap } from './events/FindMyOrder';
import { GrowthbookEventType, GrowthbookEventTypePayloadMap, GrowthbookEventSchemaMap } from './events/Growthbook';
import { NavigationEventType, NavigationEventTypePayloadMap, NavigationEventSchemaMap } from './events/Navigation';
import { OrderFormEventType, OrderFormEventTypePayloadMap, OrderFormEventSchemaMap } from './events/OrderForm';
import { ReceiptEventType, ReceiptEventTypePayloadMap, ReceiptEventSchemaMap } from './events/Receipt';
import { SignupFormEventType, SignupFormEventTypePayloadMap, SignupFormEventSchemaMap } from './events/SignupForm';
import { ProfileMenuEventType, ProfileMenuEventTypePayloadMap, ProfileMenuEventSchemaMap } from './events/ProfileMenu';
import {
  UserAnalyticsEventType,
  UserAnalyticsEventTypePayloadMap,
  UserAnalyticsEventSchemaMap,
} from './events/UserAnalytics';
import {
  VendorDashboardEventType,
  VendorDashboardEventTypePayloadMap,
  VendorDashboardEventSchemaMap,
} from './events/VendorDashboard';
import {
  ExperimentViewedEventSchemaMap,
  ExperimentViewedEventType,
  ExperimentViewedEventTypePayloadMap,
} from './events/ExperimentViewed';
import { OnboardingEventType, OnboardingEventTypePayloadMap, OnboardingEventSchemaMap } from './events/Onboarding';
import { ToggleEventType, ToggleEventTypePayloadMap, ToggleEventSchemaMap } from './events/Toggle';
import { MarketplaceEventType, MarketplaceEventTypePayloadMap, MarketplaceEventSchemaMap } from './events/Marketplace';
import {
  ShoppingCartInfoBoxEventType,
  ShoppingCartInfoBoxEventTypePayloadMap,
  ShoppingCartInfoBoxEventSchemaMap,
} from './events/ShoppingCartInfoBox';
import { UpsellEventType, UpsellEventTypePayloadMap, UpsellEventSchemaMap } from './events/Upsell';

// Define the available event types as a string literal union
export type TrackingEventType =
  | FindMyOrderEventType
  | ExampleEventType
  | ExperimentViewedEventType
  | BuyEventType
  | GrowthbookEventType
  | MarketplaceEventType
  | NavigationEventType
  | OnboardingEventType
  | OrderFormEventType
  | SignupFormEventType
  | ReceiptEventType
  | UserAnalyticsEventType
  | ProfileMenuEventType
  | VendorDashboardEventType
  | ToggleEventType
  | ShoppingCartInfoBoxEventType
  | UpsellEventType;

// Re-export the TrackingEvent type and EventPriority enum
export type { TrackingEvent } from './tracking-event';
export { EventPriority } from './tracking-event';

// Used for type inference of payloads based on their event type.
// We explicitly want to avoid interface merging because we need an immutable mapping of event types to payloads.
// eslint-disable-next-line @typescript-eslint/consistent-type-definitions
export type EventPayloadMap = FindMyOrderEventTypePayloadMap &
  ExampleEventTypePayloadMap &
  ExperimentViewedEventTypePayloadMap &
  BuyEventTypePayloadMap &
  GrowthbookEventTypePayloadMap &
  MarketplaceEventTypePayloadMap &
  NavigationEventTypePayloadMap &
  OnboardingEventTypePayloadMap &
  ProfileMenuEventTypePayloadMap &
  OrderFormEventTypePayloadMap &
  SignupFormEventTypePayloadMap &
  ReceiptEventTypePayloadMap &
  UserAnalyticsEventTypePayloadMap &
  ToggleEventTypePayloadMap &
  VendorDashboardEventTypePayloadMap &
  ShoppingCartInfoBoxEventTypePayloadMap &
  UpsellEventTypePayloadMap;

// A mapping from event type to payload schema for validation
export const EventSchemaMap: { [K in TrackingEventType]: z.ZodType<EventPayloadMap[K]> } = {
  ...FindMyOrderEventSchemaMap,
  ...ExampleEventSchemaMap,
  ...ExperimentViewedEventSchemaMap,
  ...BuyEventSchemaMap,
  ...GrowthbookEventSchemaMap,
  ...MarketplaceEventSchemaMap,
  ...NavigationEventSchemaMap,
  ...OnboardingEventSchemaMap,
  ...ProfileMenuEventSchemaMap,
  ...OrderFormEventSchemaMap,
  ...SignupFormEventSchemaMap,
  ...ReceiptEventSchemaMap,
  ...UserAnalyticsEventSchemaMap,
  ...ToggleEventSchemaMap,
  ...VendorDashboardEventSchemaMap,
  ...ShoppingCartInfoBoxEventSchemaMap,
  ...UpsellEventSchemaMap,
};
